<script lang="ts" setup>
import { computed, watch } from 'vue'
import { Switch, SwitchGroup, SwitchLabel } from '@headlessui/vue'
import { classArray } from '../../utils/class'
import type { ComplexClassName } from '../../types/common'
import { SwitchSize } from './types'

type ModelValue = boolean

interface Props {
  size?: keyof typeof SwitchSize
  disabled?: boolean
  label?: string
  labelClass?: ComplexClassName
  toggleClass?: ComplexClassName
}

const props = withDefaults(defineProps<Props>(), {
  size: SwitchSize.sm,
  disabled: false,
  labelClass: '',
})

const emit = defineEmits<{
  (e: 'change', event: ModelValue): void
}>()

const modelValue = defineModel<ModelValue>({ required: true })
watch(modelValue, v => emit('change', v))

const classesForLabel = computed((): string[] => {
  const cls = [
    'mb-0',
    'pl-3',
    'inline-block',
  ]

  if (props.disabled)
    cls.push('text-gray-300 cursor-default')
  else
    cls.push('cursor-pointer')

  return cls.concat(classArray(props.labelClass))
})
</script>

<template>
  <SwitchGroup>
    <div class="inline-flex items-center">
      <Switch
        v-slot="{ checked }"
        v-model="modelValue"
        as="button"
        class="group focus:outline-none"
        :disabled="disabled"
        :aria-checked="modelValue ? 'true' : 'false'"
      >
        <DTSwitchToggle
          :checked="checked"
          :disabled="disabled"
          :size="size"
          :class="toggleClass"
        />
      </Switch>
      <SwitchLabel
        v-if="label"
        role="presentation"
        :passive="disabled"
        :class="classesForLabel"
      >
        {{ label }}
      </SwitchLabel>
    </div>
  </SwitchGroup>
</template>
